class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
    this.summary = this.mainDetailsToggle.querySelector('summary');

    // Hover functionality
    this.hoverTimeout = null;
    this.hoverDelay = 300; // Increased delay for multi-line navigation
    this.isHovering = false; // Track if we're currently hovering

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));

    // Add hover events
    this.mainDetailsToggle.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.mainDetailsToggle.addEventListener('mouseleave', this.onMouseLeave.bind(this));

    // Add mouse move tracking to prevent closing when moving between menu items
    this.mainDetailsToggle.addEventListener('mousemove', this.onMouseMove.bind(this));

    // Add click event to summary for navigation
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));

    // Track mouse position globally to handle multi-line navigation
    this.setupGlobalMouseTracking();
  }

  setupGlobalMouseTracking() {
    // Create a more sophisticated hover area detection
    document.addEventListener('mousemove', (event) => {
      if (this.isHovering) {
        // Check if mouse is still within a reasonable area of the menu
        const menuRect = this.mainDetailsToggle.getBoundingClientRect();
        const expandedRect = {
          left: menuRect.left - 20,
          right: menuRect.right + 20,
          top: menuRect.top - 10,
          bottom: menuRect.bottom + 10,
        };

        if (
          event.clientX >= expandedRect.left &&
          event.clientX <= expandedRect.right &&
          event.clientY >= expandedRect.top &&
          event.clientY <= expandedRect.bottom
        ) {
          // Mouse is still in the expanded hover area, keep menu open
          if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = null;
          }
        }
      }
    });
  }

  onMouseEnter() {
    // Clear any pending close timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    this.isHovering = true;

    // Open the menu on hover
    if (!this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.setAttribute('open', '');
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
    }
  }

  onMouseMove(event) {
    // If we're hovering and the mouse moves, keep the menu open
    if (this.isHovering) {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
    }
  }

  onMouseLeave() {
    this.isHovering = false;

    // Set a delay before closing to allow moving to submenu items
    // and to handle gaps between multi-line menu items
    this.hoverTimeout = setTimeout(() => {
      // Only close if we're still not hovering
      if (!this.isHovering) {
        this.close();
      }
    }, this.hoverDelay);
  }

  onSummaryClick(event) {
    // Check if the summary has a data-url attribute
    const dataUrl = this.summary.getAttribute('data-url');
    if (dataUrl) {
      // Prevent the default details toggle behavior
      event.preventDefault();
      // Navigate to the URL
      window.location.href = dataUrl;
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onMouseEnter() {
    super.onMouseEnter();
    // Update header positioning on hover
    this.updateHeaderPosition();
  }

  onMouseLeave() {
    super.onMouseLeave();
    // Clear header positioning on hover leave
    if (this.header) {
      this.header.preventHide = false;
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    this.updateHeaderPosition();
  }

  updateHeaderPosition() {
    if (!this.header) return;

    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
